// Lightweight cookie consent banner.
// Categories: necessary (always on), functional, analytics.
// Stores choice in localStorage 'cosec-consent-v1'.
import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";
import { X, Cookie, Settings2 } from "lucide-react";

const KEY = "cosec-consent-v1";

function readConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function writeConsent(obj) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...obj, savedAt: new Date().toISOString() }));
  } catch (e) {
    // ignore
  }
  // Broadcast to other tabs/components
  window.dispatchEvent(new CustomEvent("cosec-consent", { detail: obj }));
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, functional: true, analytics: false });

  useEffect(() => {
    const c = readConsent();
    queueMicrotask(() => {
      if (!c) {
        setOpen(true);
      } else if (c.functional !== prefs.functional || c.analytics !== prefs.analytics) {
        setPrefs({ necessary: true, functional: !!c.functional, analytics: !!c.analytics });
      }
    });
    const onOpen = () => { queueMicrotask(() => { setOpen(true); setExpanded(true); }); };
    window.addEventListener("cosec-open-cookie-prefs", onOpen);
    return () => window.removeEventListener("cosec-open-cookie-prefs", onOpen);
  }, []);

  const acceptAll = useCallback(() => {
    const p = { necessary: true, functional: true, analytics: true };
    setPrefs(p);
    writeConsent(p);
    setOpen(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    const p = { necessary: true, functional: false, analytics: false };
    setPrefs(p);
    writeConsent(p);
    setOpen(false);
  }, []);

  const savePrefs = useCallback(() => {
    writeConsent(prefs);
    setOpen(false);
  }, [prefs]);

  if (!open) return null;

  return (
    <div data-testid="cookie-banner" className="fixed bottom-4 left-4 right-4 md:right-auto md:left-6 md:bottom-6 z-[60] max-w-md">
      <div className="rounded-2xl border border-border bg-white shadow-[0_24px_60px_-24px_rgba(14,58,95,0.35)] overflow-hidden">
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 grid place-items-center rounded-lg bg-accent/15 text-primary">
                <Cookie className="h-4 w-4" />
              </div>
              <div>
                <div className="font-display font-bold text-primary">Your cookie preferences</div>
                <div className="text-[11px] text-muted-foreground">We use cookies to run this site &amp; understand what works.</div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="cookie-close"
              aria-label="Close"
              className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:bg-secondary"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {!expanded ? (
            <div className="mt-4 text-sm text-muted-foreground leading-relaxed">
              We use strictly necessary cookies to make the site work. With your permission, we also use functional and analytics cookies to improve it. See our{" "}
              <Link to="/cookies" className="text-primary font-semibold underline-offset-2 hover:underline">Cookies Policy</Link>.
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              <PrefRow
                title="Strictly necessary"
                desc="Required for the site to work, authentication, security, basic functionality."
                checked
                disabled
              />
              <PrefRow
                title="Functional"
                desc="Remembers your preferences (e.g. pricing toggle, dismissed banners)."
                checked={prefs.functional}
                onChange={(v) => setPrefs({ ...prefs, functional: v })}
                testid="cookie-functional"
              />
              <PrefRow
                title="Analytics"
                desc="Helps us understand how visitors use the site so we can improve it. Anonymised."
                checked={prefs.analytics}
                onChange={(v) => setPrefs({ ...prefs, analytics: v })}
                testid="cookie-analytics"
              />
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            {!expanded ? (
              <>
                <Button
                  data-testid="cookie-accept-all"
                  onClick={acceptAll}
                  className="flex-1 h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  Accept all
                </Button>
                <Button
                  data-testid="cookie-reject"
                  onClick={rejectNonEssential}
                  variant="outline"
                  className="flex-1 h-10 border-primary/20 text-primary font-semibold"
                >
                  Reject non-essential
                </Button>
              </>
            ) : (
              <>
                <Button
                  data-testid="cookie-save"
                  onClick={savePrefs}
                  className="flex-1 h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  Save preferences
                </Button>
                <Button
                  data-testid="cookie-accept-all-2"
                  onClick={acceptAll}
                  variant="outline"
                  className="flex-1 h-10 border-primary/20 text-primary font-semibold"
                >
                  Accept all
                </Button>
              </>
            )}
          </div>

          {!expanded && (
            <button
              data-testid="cookie-customise"
              onClick={() => setExpanded(true)}
              className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary hover:text-accent transition-colors"
            >
              <Settings2 className="h-3.5 w-3.5" /> Customise
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled, testid }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3 flex items-start gap-3">
      <div className="flex-1">
        <div className="text-sm font-display font-bold text-primary">{title}</div>
        <div className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">{desc}</div>
      </div>
      <Switch
        data-testid={testid}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}
